import { useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

/**
 * Sticky bottom banner surfacing "new version available" when the PWA
 * service worker detects an update. Tap = full reload onto the new
 * build (skipWaiting + window.location.reload via vite-plugin-pwa).
 *
 * Why a banner instead of letting `registerType: 'autoUpdate'` swap
 * silently: tournament-day. A player can keep the page open for hours
 * during a round. The "silent update on next navigation" path means
 * deploys land somewhere between "minutes from now" and "after the
 * round." The banner makes the update moment explicit so the floor
 * can hand-off a known version per device.
 *
 * First-install guard: the banner must fire ONLY for a genuine update to an
 * already-installed app — never on the very first SW install (opening the
 * link for the first time), where surfacing "ny versjon" for the build you
 * just loaded is nonsensical and alarming mid-event. We snapshot a
 * localStorage marker at mount (before registration sets it): if the app was
 * already installed on this device in a prior session, a `needRefresh` is a
 * real update and we show the banner; if not, this is the first install and
 * we stay silent while recording the marker for next time.
 *
 * Positioning: floats above BottomNav using the same `--bottom-nav-h`
 * + safe-area inset that the main scroll area uses, so the banner
 * never hides nav tabs or gets clipped by an iPhone home bar.
 */
const INSTALLED_KEY = 'polf-pwa-installed'

const UpdatePrompt = () => {
  const [installedBefore] = useState(() => {
    // Canonical first-install signal: an already-installed app's page is
    // controlled by its service worker from load (`controller` set); a
    // first-ever load is uncontrolled (`controller` null). More reliable than
    // the localStorage marker alone (which can be cleared or storage-blocked),
    // so OR the two. Both are false on a genuinely fresh device, so the update
    // banner still cannot fire on a first load.
    const controlled =
      typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller
    try {
      return controlled || localStorage.getItem(INSTALLED_KEY) === '1'
    } catch {
      // Private-mode / storage-blocked: fall back to the controller signal
      // alone rather than risk a spurious first-load banner.
      return controlled
    }
  })
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW() {
      try {
        localStorage.setItem(INSTALLED_KEY, '1')
      } catch {
        /* storage blocked — banner simply stays suppressed on this device */
      }
    },
  })

  if (!needRefresh || !installedBefore) return null

  return (
    <button
      type="button"
      onClick={() => updateServiceWorker(true)}
      className="fixed left-0 right-0 z-50 bg-green-700 text-paper px-6 py-3 font-display font-semibold text-sm text-center shadow-lg active:bg-green-900 transition-colors"
      style={{
        bottom: `calc(var(--bottom-nav-h) + env(safe-area-inset-bottom))`,
        minHeight: 44,
      }}
    >
      Ny versjon tilgjengelig — trykk for å oppdatere
    </button>
  )
}

export default UpdatePrompt
